/**
 * Slide-over showing the instalments behind one cash-flow timeline
 * bar (month or quarter), split billed vs scheduled.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, X, ExternalLink } from "lucide-react";
import { fmtGbpShort } from "../staff/CommissionedPanels";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtTitle = (p) =>
  p.includes("Q")
    ? `${p.slice(5)} ${p.slice(0, 4)}`
    : new Date(+p.slice(0, 4), +p.slice(5, 7) - 1, 1)
        .toLocaleDateString("en-GB", { month: "long", year: "numeric" });

export default function TimelineDrill({ period, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    setData(null);
    setError("");
    axios.get(`${API}/api/proposals/pipeline/revenue-timeline/detail`, { params: { period } })
      .then((r) => { if (live) setData(r.data); })
      .catch((e) => { if (live) setError(e.response?.data?.detail || "Could not load the detail."); });
    return () => { live = false; };
  }, [period]);

  return (
    <div className="fixed inset-0 z-50" data-testid="timeline-drill">
      <div className="absolute inset-0 bg-slate-900/30" onClick={onClose} data-testid="timeline-drill-backdrop" />
      <aside className="absolute inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl flex flex-col">
        <header className="px-5 py-4 border-b border-stone-200 flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-wider text-stone-400">Cash-flow detail</p>
            <h3 className="font-serif text-xl text-slate-900 leading-tight">{fmtTitle(period)}</h3>
            {data && (
              <p className="text-xs text-stone-500 mt-1">
                {data.count} instalment{data.count === 1 ? "" : "s"}
                {data.billed_total !== 0 && <> · <span className="font-mono text-teal-700">{fmtGbpShort(data.billed_total)} billed</span></>}
                {data.scheduled_total !== 0 && <> · <span className="font-mono text-sky-700">{fmtGbpShort(data.scheduled_total)} scheduled</span></>}
              </p>
            )}
          </div>
          <button type="button" onClick={onClose} data-testid="timeline-drill-close"
            className="p-1.5 rounded-lg text-stone-400 hover:text-slate-800 hover:bg-stone-100 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {!data && !error && (
            <div className="flex items-center gap-2 text-stone-500 text-sm py-6">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}
          {error && <p className="text-sm text-amber-800 py-4">{error}</p>}
          {data && (
            <ul className="divide-y divide-stone-100">
              {data.items.map((i, idx) => (
                <li key={`${i.fiid || idx}`} className="py-2.5 flex items-start gap-3">
                  <span className={`mt-1 inline-block w-2 h-2 rounded-full shrink-0 ${i.billed ? "bg-teal-600" : "bg-sky-400"}`}
                    title={i.billed ? "billed" : "scheduled"} />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-slate-800 truncate">
                      {i.client} <span className="text-stone-400 font-normal">· {i.project_name}</span>
                    </p>
                    <p className="text-[10px] font-mono text-stone-400 mt-0.5">
                      {i.date} · {i.status}{i.pm ? ` · PM ${i.pm}` : ""} · {i.team}
                      {i.fiid ? ` · ${i.fiid}` : ""}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-xs font-mono tabular-nums ${i.value < 0 ? "text-rose-700" : "text-slate-800"}`}>{fmtGbpShort(i.value)}</p>
                    {i.gp != null && <p className="text-[10px] font-mono text-stone-400">GP {fmtGbpShort(i.gp)}</p>}
                  </div>
                  {i.cognito_admin && (
                    <a href={i.cognito_admin} target="_blank" rel="noreferrer"
                      className="mt-0.5 text-stone-300 hover:text-[#1B4F8A] transition-colors shrink-0">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}
